// Lightweight client-side computation of "high street equivalent" prices.
// Used to surface diaspora-friendly savings badges on curated AfriMarket items
// (e.g. "Save £18 vs UK high street"). Only applied to is_curated listings so
// we never imply savings on services or third-party seller items.
//
// Multipliers chosen from typical UK retail markups in 2025-2026:
//  - Fashion / Ankara / wax-print: 1.7× (ASOS / Boohoo benchmark)
//  - Beauty / haircare: 1.6× (Boots / Superdrug)
//  - Electronics: 1.3× (Argos / Currys)
//  - Home: 1.5× (Dunelm / John Lewis)
//  - Food / spices: 1.4× (specialty grocer markup)
const MULT = {
  fashion: 1.7,
  clothing: 1.7,
  beauty: 1.6,
  haircare: 1.6,
  electronics: 1.3,
  tech: 1.3,
  home: 1.5,
  homeware: 1.5,
  food: 1.4,
  grocery: 1.4,
};
const DEFAULT_MULT = 1.5;

const categoryMult = (category) => {
  if (!category) return DEFAULT_MULT;
  const key = String(category).toLowerCase();
  // Substring match — covers "Women's Fashion", "Beauty & Skincare" etc.
  for (const [k, v] of Object.entries(MULT)) {
    if (key.includes(k)) return v;
  }
  return DEFAULT_MULT;
};

/**
 * Returns null when the saving isn't worth surfacing (item too cheap,
 * non-curated, or no category match). Caller renders nothing if null.
 *
 * @param {number} price
 * @param {string} category
 * @param {object} opts  { onlyIfCurated, is_curated }
 * @returns {{ retail:number, savings:number, percent:number } | null}
 */
export function highStreetSavings(price, category, opts = {}) {
  if (opts.onlyIfCurated && !opts.is_curated) return null;
  const p = Number(price);
  if (!Number.isFinite(p) || p < 5) return null;
  const mult = categoryMult(category);
  const retail = p * mult;
  const savings = retail - p;
  if (savings < 3) return null; // not worth shouting about
  const percent = Math.round((1 - 1 / mult) * 100);
  return { retail, savings, percent };
}
